import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';

const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

const ChangePermissionSetsModal = ({
  account,
  selectedAssignmentIds,
  assignments,
  permissionSets = [],
  onAddPermissionSet,
  onRemoveAssignment,
  onClose,
}) => {
  const [addPermissionSetIds, setAddPermissionSetIds] = useState([]);
  const [removeAssignmentIds, setRemoveAssignmentIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const accountId = account.accountId || account.account_id;
  const accountName = account.accountName || account.name || accountId;

  // Get selected principals from assignments
  const selectedPrincipals = useMemo(() => {
    const principalMap = new Map();

    assignments.forEach((assignment) => {
      if (selectedAssignmentIds.includes(assignment.id)) {
        const key = `${assignment.principalType}:${assignment.username || assignment.groupName || assignment.principalName}`;
        if (!principalMap.has(key)) {
          principalMap.set(key, {
            principalType: assignment.principalType,
            principalName: assignment.username || assignment.groupName || assignment.principalName,
            permissionSets: [],
            assignmentIds: [],
          });
        }
        const principal = principalMap.get(key);
        principal.permissionSets.push({
          id: assignment.permissionSetId,
          name: assignment.permissionSetName,
          assignmentId: assignment.id,
        });
        principal.assignmentIds.push(assignment.id);
      }
    });

    return Array.from(principalMap.values());
  }, [assignments, selectedAssignmentIds]);

  // Get current permission set IDs for selected principals
  const currentPermissionSetIds = useMemo(() => {
    const ids = new Set();
    selectedPrincipals.forEach((p) => {
      p.permissionSets.forEach((ps) => ids.add(ps.id));
    });
    return Array.from(ids);
  }, [selectedPrincipals]);

  // Get available permission sets to add (not already assigned to all selected principals)
  const availableToAdd = useMemo(() => {
    return permissionSets.filter((ps) => !currentPermissionSetIds.includes(ps.id));
  }, [permissionSets, currentPermissionSetIds]);

  // Get permission sets that can be removed
  const removablePermissionSets = useMemo(() => {
    const removable = [];
    selectedPrincipals.forEach((p) => {
      p.permissionSets.forEach((ps) => {
        if (!removable.find((r) => r.id === ps.id)) {
          removable.push({
            id: ps.id,
            name: ps.name,
            assignmentIds: [ps.assignmentId],
          });
        } else {
          const existing = removable.find((r) => r.id === ps.id);
          existing.assignmentIds.push(ps.assignmentId);
        }
      });
    });
    return removable;
  }, [selectedPrincipals]);

  const handleAddChange = (e) => {
    setAddPermissionSetIds(e.target.value);
    setError('');
  };

  const handleToggleRemove = (assignmentIds) => {
    const allIncluded = assignmentIds.every((id) => removeAssignmentIds.includes(id));
    if (allIncluded) {
      setRemoveAssignmentIds(removeAssignmentIds.filter((id) => !assignmentIds.includes(id)));
    } else {
      setRemoveAssignmentIds([...new Set([...removeAssignmentIds, ...assignmentIds])]);
    }
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (addPermissionSetIds.length === 0 && removeAssignmentIds.length === 0) {
      setError('Please select permission sets to add or remove');
      return;
    }

    // If there are removals, show confirmation dialog
    if (removeAssignmentIds.length > 0) {
      setShowConfirmDialog(true);
      return;
    }

    // Otherwise, proceed directly
    performSubmit();
  };

  const performSubmit = async () => {
    setLoading(true);
    setShowConfirmDialog(false);

    try {
      let success = true;

      // Add new permission sets
      if (addPermissionSetIds.length > 0 && onAddPermissionSet) {
        for (const principal of selectedPrincipals) {
          for (const psId of addPermissionSetIds) {
            const assignmentData = {
              permissionSetId: psId,
              principalType: principal.principalType,
              accountId: [accountId],
            };

            if (principal.principalType === 'USER') {
              assignmentData.username = principal.principalName;
            } else {
              assignmentData.groupName = principal.principalName;
            }

            const result = await onAddPermissionSet(assignmentData);
            if (!result) success = false;
          }
        }
      }

      // Remove selected assignments
      if (removeAssignmentIds.length > 0 && onRemoveAssignment) {
        for (const assignmentId of removeAssignmentIds) {
          const result = await onRemoveAssignment(assignmentId);
          if (!result) success = false;
        }
      }

      if (success) {
        onClose();
      }
    } catch (err) {
      setError('Failed to update permission sets');
    } finally {
      setLoading(false);
    }
  };

  // Get the names of permission sets being removed for the confirmation message
  const getRemovalPermissionSetNames = () => {
    return removablePermissionSets
      .filter((ps) => ps.assignmentIds.some((id) => removeAssignmentIds.includes(id)))
      .map((ps) => ps.name);
  };

  return (
    <Modal onClose={onClose}>
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#F5EBF8',
            borderRadius: '12px 12px 0 0',
            p: 3,
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#9B59B6',
                width: 48,
                height: 48,
              }}
            >
              <EditIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: '#253E66',
                }}
              >
                Change permission sets
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                }}
              >
                Modify access for selected users/groups
              </Typography>
            </Box>
          </Box>
        </Paper>
        </motion.div>

        {/* Form Content */}
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '0 0 12px 12px',
            p: 3,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Selected Principals Summary */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: '#253E66',
                  mb: 1.5,
                }}
              >
                Selected {selectedPrincipals.length === 1 ? 'principal' : 'principals'}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  p: 2,
                }}
              >
                {selectedPrincipals.map((principal, index) => (
                  <Box
                    key={`${principal.principalType}:${principal.principalName}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: index < selectedPrincipals.length - 1 ? 1 : 0,
                    }}
                  >
                    {principal.principalType === 'USER' ? (
                      <PersonIcon sx={{ fontSize: 18, color: '#4698D3' }} />
                    ) : (
                      <GroupIcon sx={{ fontSize: 18, color: '#9B59B6' }} />
                    )}
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.9rem',
                        color: '#253E66',
                        fontWeight: 500,
                      }}
                    >
                      {principal.principalName}
                    </Typography>
                    <Chip
                      label={principal.principalType}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor:
                          principal.principalType === 'USER' ? '#EBF5FB' : '#F5EBF8',
                        color: principal.principalType === 'USER' ? '#4698D3' : '#9B59B6',
                      }}
                    />
                  </Box>
                ))}
              </Paper>
            </Box>
            </motion.div>

            <Divider sx={{ my: 3 }} />

            {/* Add Permission Sets */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AddIcon sx={{ color: '#27AE60', fontSize: 22 }} />
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#253E66',
                  }}
                >
                  Add permission sets
                </Typography>
              </Box>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#666666',
                    '&.Mui-focused': { color: '#27AE60' },
                  }}
                >
                  Select permission sets to add
                </InputLabel>
                <Select
                  multiple
                  value={addPermissionSetIds}
                  onChange={handleAddChange}
                  label="Select permission sets to add"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const ps = permissionSets.find((p) => p.id === id);
                        return (
                          <Chip
                            key={id}
                            label={ps?.name || id}
                            size="small"
                            sx={{
                              backgroundColor: '#F0F9F4',
                              color: '#27AE60',
                              border: '1px solid #27AE6030',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E5E5',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#27AE60',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#27AE60',
                    },
                  }}
                >
                  {availableToAdd.length === 0 ? (
                    <MenuItem
                      disabled
                      sx={{
                        fontStyle: 'italic',
                        color: '#666666',
                        fontFamily: 'Metropolis, sans-serif',
                      }}
                    >
                      All permission sets already assigned
                    </MenuItem>
                  ) : (
                    availableToAdd.map((ps) => (
                      <MenuItem key={ps.id} value={ps.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SecurityIcon sx={{ fontSize: 20, color: '#27AE60' }} />
                          {ps.name}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
            </motion.div>

            {/* Remove Permission Sets */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RemoveIcon sx={{ color: '#E74C3C', fontSize: 22 }} />
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#253E66',
                  }}
                >
                  Remove permission sets
                </Typography>
              </Box>
              {removablePermissionSets.length === 0 ? (
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#666666',
                    fontStyle: 'italic',
                  }}
                >
                  No permission sets to remove
                </Typography>
              ) : (
                <List
                  sx={{
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    p: 0,
                    overflow: 'hidden',
                  }}
                >
                  {removablePermissionSets.map((ps, index) => {
                    const isSelected = ps.assignmentIds.every((id) =>
                      removeAssignmentIds.includes(id)
                    );
                    return (
                      <ListItem
                        key={ps.id}
                        button
                        onClick={() => handleToggleRemove(ps.assignmentIds)}
                        sx={{
                          borderBottom:
                            index < removablePermissionSets.length - 1
                              ? '1px solid #E5E5E5'
                              : 'none',
                          backgroundColor: isSelected ? '#FDF2F2' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? '#FDF2F2' : '#F9FBFD',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <SecurityIcon
                            sx={{
                              fontSize: 20,
                              color: isSelected ? '#E74C3C' : '#9B59B6',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={ps.name}
                          primaryTypographyProps={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.9rem',
                            color: isSelected ? '#E74C3C' : '#253E66',
                            fontWeight: 500,
                            textDecoration: isSelected ? 'line-through' : 'none',
                          }}
                        />
                        {isSelected && (
                          <Chip
                            label="Will be removed"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              backgroundColor: '#FDF2F2',
                              color: '#E74C3C',
                              border: '1px solid #E74C3C30',
                            }}
                          />
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  color: '#999999',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  mt: 1,
                  fontStyle: 'italic',
                }}
              >
                Click on a permission set to mark it for removal
              </Typography>
            </Box>
            </motion.div>

            {/* Form Actions */}
            <motion.div variants={itemVariants}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                type="button"
                variant="outlined"
                onClick={onClose}
                disabled={loading}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  borderColor: '#E5E5E5',
                  color: '#666666',
                  '&:hover': {
                    borderColor: '#9B59B6',
                    color: '#9B59B6',
                    backgroundColor: '#F5EBF8',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#E5E5E5',
                    color: '#999999',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  loading ||
                  (addPermissionSetIds.length === 0 && removeAssignmentIds.length === 0)
                }
                startIcon={
                  loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <EditIcon />
                }
                sx={{
                  backgroundColor: '#9B59B6',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#8E44AD',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#E5E5E5',
                    color: '#999999',
                  },
                }}
              >
                {loading ? 'Saving...' : 'Save changes'}
              </Button>
            </Box>
            </motion.div>
          </Box>
        </Box>
      </motion.div>

      {/* Confirmation Dialog for Removals */}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Confirm Changes"
        message={`Are you sure you want to remove the following permission set${getRemovalPermissionSetNames().length > 1 ? 's' : ''}: ${getRemovalPermissionSetNames().join(', ')}? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={loading}
        onConfirm={performSubmit}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </Modal>
  );
};

export default ChangePermissionSetsModal;
